import React, { useState, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import { css, SerializedStyles } from '@emotion/react';
import SPImage from '@components/commons/image/SPImage';

interface IChatItem {
  imageWidth?: number;
  imageHeight?: number;
  imageURL?: string;
  setImageSize: (
    width: number,
    height: number,
    startX?: number,
    startY?: number,
  ) => void;
  children: JSX.Element | JSX.Element[];
  onClickImage?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  hasTopMargin: boolean;
}

const FullImageContainer = ({
  imageWidth,
  imageHeight,
  imageURL,
  setImageSize,
  onClickImage,
  children,
  hasTopMargin,
}: IChatItem) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const [imageWrapper, setImageWrapper] = useState<SerializedStyles>(css`
    position: relative;
    width: 100%;
    height: 100%;
  `);

  useEffect(() => {
    onResize();
    window.addEventListener('resize', windowOnChange);
    return () => {
      window.removeEventListener('resize', windowOnChange);
    };
  }, []);

  const windowOnChange = debounce(() => onResize(), 200);

  const onResize = () => {
    if (parentRef.current && imageRef.current && imageWidth && imageHeight) {
      const parentWidth = parentRef.current.offsetWidth;
      const parentHeight = parentRef.current.offsetHeight;

      const widthInProportion = parentHeight * (imageWidth / imageHeight);
      const heightInProportion = parentWidth * (imageHeight / imageWidth);

      if (parentHeight) {
        if (heightInProportion > parentHeight) {
          setImageSize(
            widthInProportion,
            parentHeight,
            (parentWidth - widthInProportion) / 2,
            0,
          );
          setImageWrapper(imageWrapperStyle(parentHeight));
        } else {
          setImageSize(parentWidth, heightInProportion, 0, 0);
          setImageWrapper(imageWrapperStyle(heightInProportion));
        }
      } else {
        setImageSize(
          imageRef.current?.offsetWidth,
          heightInProportion,
          (parentWidth - imageRef.current?.offsetWidth) / 2,
          (parentHeight - heightInProportion) / 2,
        );
        setImageWrapper(imageWrapperStyle(heightInProportion));
      }
    }
  };

  return (
    <div css={full(hasTopMargin)} ref={parentRef}>
      {imageURL && (
        <div css={imageWrapper} ref={imageRef} onClick={onClickImage}>
          <SPImage
            src={imageURL}
            layout="fill"
            objectFit="contain"
            alt="Full chat image"
            sizes="(max-width: 800px) 50vw, 33vw"
          />
          {children}
        </div>
      )}
    </div>
  );
};

const defaultStyle = css`
  display: flex;
  max-width: 100%;
  object-fit: contain;
`;

const full = (hasTopMargin: boolean) => css`
  ${defaultStyle}
  width: 100%;
  height: 100%;
  align-items: center;
  margin-top: ${hasTopMargin ? '1.5em' : '0'};
  justify-content: center;
`;

const imageWrapperStyle = (height: number) => css`
  position: relative;
  width: 100%;
  height: ${height}px;
`;

export default FullImageContainer;
